import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { STATIC_URL } from '../services/api';
import { User, Shield, Check, AlertCircle, FileText, Download, Upload, Database, Tag, Plus, Trash2 } from 'lucide-react';

const Settings = () => {
    const { user, login, currency, setCurrency } = useAuth(); // We might need to update the user in context after edit

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        currency: 'EUR'
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Invoice Profile State
    const [invoiceProfile, setInvoiceProfile] = useState<any>(null);
    const [invoiceFormData, setInvoiceFormData] = useState({
        profileName: 'Default Profile',
        profileType: 'Auto-Entrepreneur',
        companyName: '',
        address: '',
        email: '',
        phone: '',
        cnie: '',
        ice: '',
        taxIf: '',
        professionalTaxNo: '',
        rc: '',
        logoUrl: '',
        signatureUrl: ''
    });
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceMessage, setInvoiceMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Data Management State
    const [dataLoading, setDataLoading] = useState(false);
    const [dataMessage, setDataMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Expense Categories State
    const [categories, setCategories] = useState<string[]>([]);
    const [newCategory, setNewCategory] = useState('');
    const [catMessage, setCatMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.name,
                email: user.email,
                currency: currency || 'EUR'
            }));
        }
    }, [user, currency]);

    useEffect(() => {
        api.get('/settings').then(res => {
            if (res.data.expenseCategories) {
                setCategories(res.data.expenseCategories);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const fetchInvoiceProfile = async () => {
            try {
                const res = await api.get('/invoice-profiles');
                if (res.data && res.data.length > 0) {
                    const profile = res.data[0];
                    setInvoiceProfile(profile);
                    setInvoiceFormData({
                        profileName: profile.profileName || '',
                        profileType: profile.profileType || 'Auto-Entrepreneur',
                        companyName: profile.companyName || '',
                        address: profile.address || '',
                        email: profile.email || '',
                        phone: profile.phone || '',
                        cnie: profile.cnie || '',
                        ice: profile.ice || '',
                        taxIf: profile.taxIf || '',
                        professionalTaxNo: profile.professionalTaxNo || '',
                        rc: profile.rc || '',
                        logoUrl: profile.logoUrl || '',
                        signatureUrl: profile.signatureUrl || ''
                    });
                }
            } catch (err) {
                console.error('Error fetching invoice profile:', err);
            }
        };

        if (user) {
            fetchInvoiceProfile();
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        if (formData.password && formData.password !== formData.confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            setLoading(false);
            return;
        }

        try {
            await Promise.all([
                api.put('/auth/me', {
                    name: formData.name,
                    email: formData.email,
                    ...(formData.password ? { password: formData.password } : {})
                }),
                api.put('/settings', {
                    currency: formData.currency
                })
            ]);

            // Refresh user data in context
            const token = localStorage.getItem('token');
            if (token) {
                login(token); // This triggers a fetch of /auth/me
            }
            setCurrency(formData.currency); // Update local context immediately

            setMessage({ type: 'success', text: 'Settings updated successfully' });
        } catch (err: any) {
            console.error('Update Error:', err);
            const errorMsg = err.response?.data?.message || err.message || 'Error updating settings';
            setMessage({
                type: 'error',
                text: errorMsg
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInvoiceSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setInvoiceLoading(true);
        setInvoiceMessage(null);

        try {
            if (invoiceProfile) {
                await api.put(`/invoice-profiles/${invoiceProfile.id}`, invoiceFormData);
                setInvoiceMessage({ type: 'success', text: 'Invoice profile updated successfully' });
            } else {
                const res = await api.post('/invoice-profiles', invoiceFormData);
                setInvoiceProfile(res.data);
                setInvoiceMessage({ type: 'success', text: 'Invoice profile created successfully' });
            }
        } catch (err: any) {
            console.error('Invoice Profile Error:', err);
            const errorMsg = err.response?.data?.message || err.message || 'Error updating invoice profile';
            setInvoiceMessage({ type: 'error', text: errorMsg });
        } finally {
            setInvoiceLoading(false);
        }
    };

    const ensureProfile = async (): Promise<string | null> => {
        if (invoiceProfile?.id) return invoiceProfile.id;
        try {
            const res = await api.post('/invoice-profiles', {
                ...invoiceFormData,
                profileName: invoiceFormData.profileName || 'Default Profile',
                companyName: invoiceFormData.companyName || '-',
                address: invoiceFormData.address || '-',
                email: invoiceFormData.email || (user?.email || '-'),
            });
            setInvoiceProfile(res.data);
            return res.data.id;
        } catch {
            return null;
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        setInvoiceLoading(true);
        const profileId = await ensureProfile();
        if (!profileId) {
            setInvoiceMessage({ type: 'error', text: 'Failed to initialize profile.' });
            setInvoiceLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('logo', file);

        try {
            const res = await api.post(`/invoice-profiles/${profileId}/upload-logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setInvoiceFormData(prev => ({ ...prev, logoUrl: res.data.logoUrl }));
            setInvoiceProfile(res.data.profile);
            setInvoiceMessage({ type: 'success', text: 'Logo uploaded successfully' });
        } catch (err: any) {
            console.error('Logo upload error:', err);
            setInvoiceMessage({ type: 'error', text: 'Failed to upload logo' });
        } finally {
            setInvoiceLoading(false);
        }
    };

    const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        setInvoiceLoading(true);
        const profileId = await ensureProfile();
        if (!profileId) {
            setInvoiceMessage({ type: 'error', text: 'Failed to initialize profile.' });
            setInvoiceLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('signature', file);

        try {
            const res = await api.post(`/invoice-profiles/${profileId}/upload-signature`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setInvoiceFormData(prev => ({ ...prev, signatureUrl: res.data.signatureUrl }));
            setInvoiceProfile(res.data.profile);
            setInvoiceMessage({ type: 'success', text: 'Signature uploaded successfully' });
        } catch (err: any) {
            console.error('Signature upload error:', err);
            setInvoiceMessage({ type: 'error', text: 'Failed to upload signature' });
        } finally {
            setInvoiceLoading(false);
        }
    };

    const handleAddCategory = async () => {
        const trimmed = newCategory.trim();
        if (!trimmed || categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) return;
        const updated = [...categories, trimmed];
        try {
            await api.put('/settings', { expenseCategories: updated });
            setCategories(updated);
            setNewCategory('');
            setCatMessage({ type: 'success', text: 'Category added' });
        } catch { setCatMessage({ type: 'error', text: 'Failed to save' }); }
        setTimeout(() => setCatMessage(null), 2000);
    };

    const handleDeleteCategory = async (cat: string) => {
        const updated = categories.filter(c => c !== cat);
        try {
            await api.put('/settings', { expenseCategories: updated });
            setCategories(updated);
            setCatMessage({ type: 'success', text: `"${cat}" deleted. Existing expenses with this category still appear in reports until you update them manually.` });
        } catch { setCatMessage({ type: 'error', text: 'Failed to delete' }); }
        setTimeout(() => setCatMessage(null), 5000);
    };

    const handleExportData = async () => {
        setDataLoading(true);
        setDataMessage(null);
        try {
            const res = await api.get('/data/export');
            const dataStr = JSON.stringify(res.data, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            const exportFileDefaultName = `billing_backup_${new Date().toISOString().split('T')[0]}.json`;

            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', url);
            linkElement.setAttribute('download', exportFileDefaultName);
            linkElement.click();
            URL.revokeObjectURL(url);

            setDataMessage({ type: 'success', text: 'Data exported successfully!' });
        } catch (err: any) {
            console.error('Export Error:', err);
            setDataMessage({ type: 'error', text: 'Failed to export data.' });
        } finally {
            setDataLoading(false);
        }
    };

    const handleImportData = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        if (!window.confirm('WARNING: Importing data will replace your current data. This cannot be undone. Are you sure?')) {
            e.target.value = ''; // Reset input
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            setDataLoading(true);
            setDataMessage(null);
            try {
                const json = JSON.parse(event.target?.result as string);
                await api.post('/data/import', json);
                setDataMessage({ type: 'success', text: 'Data imported successfully! Please refresh the page.' });

                setTimeout(() => window.location.reload(), 2000);
            } catch (err: any) {
                console.error('Import Error:', err);
                setDataMessage({ type: 'error', text: err.response?.data?.message || 'Failed to import data. Ensure the file is a valid backup.' });
            } finally {
                setDataLoading(false);
            }
        };
        reader.readAsText(file);
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <User size={20} className="text-blue-600" />
                        Profile Information
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Manage your account details.</p>
                </div>

                <div className="p-6 space-y-6">
                    {message && (
                        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                            {message.text}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        {user?.role?.toLowerCase() === 'admin' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <div className="flex items-center gap-2 text-gray-700 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                                <Shield size={16} className="text-green-600" />
                                <span className="capitalize">{user?.role}</span>
                            </div>
                        </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">New Password (Optional)</label>
                            <input
                                type="password"
                                value={formData.password}
                                onChange={e => setFormData({ ...formData, password: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
                            <select
                                value={formData.currency}
                                onChange={e => setFormData({ ...formData, currency: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition bg-white"
                            >
                                <option value="EUR">Euro (€)</option>
                                <option value="USD">US Dollar ($)</option>
                                <option value="GBP">British Pound (£)</option>
                                <option value="MAD">Moroccan Dirham (MAD)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                            <input
                                type="password"
                                value={formData.confirmPassword}
                                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                placeholder="Confirm new password"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition ${loading ? 'opacity-70 cursor-wait' : ''
                                }`}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>

            <form onSubmit={handleInvoiceSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <FileText size={20} className="text-blue-600" />
                        Invoice Profile
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">These details will appear on generated invoices.</p>
                    <div className="flex gap-3 mt-3">
                        {['Auto-Entrepreneur', 'Entreprise'].map(type => (
                            <button
                                key={type}
                                type="button"
                                onClick={() => setInvoiceFormData(prev => ({ ...prev, profileType: type }))}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition ${invoiceFormData.profileType === type ? 'bg-blue-600 text-white border-blue-600' : 'text-gray-600 border-gray-300 hover:border-blue-400'}`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {invoiceMessage && (
                        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${invoiceMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {invoiceMessage.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                            {invoiceMessage.text}
                        </div>
                    )}

                    {/* Logo Upload Section */}
                    <div className="mb-6 bg-gray-50 p-4 border border-gray-200 rounded-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            {invoiceFormData.logoUrl ? (
                                <div className="w-32 h-32 border border-gray-300 rounded-md bg-white flex items-center justify-center p-2 relative group">
                                    <img src={`${STATIC_URL}${invoiceFormData.logoUrl}`} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                                </div>
                            ) : (
                                <div className="w-32 h-32 border border-dashed border-gray-400 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                                    No Logo
                                </div>
                            )}
                            <div>
                                <label className="cursor-pointer bg-white px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition inline-flex items-center">
                                    {invoiceFormData.logoUrl ? 'Change Logo' : 'Upload Logo'}
                                    <input type="file" className="hidden" accept="image/png, image/jpeg, image/jpg, image/svg+xml" onChange={handleLogoUpload} />
                                </label>
                                <p className="text-xs text-gray-500 mt-2">Recommended: PNG, JPG (Max 5MB)</p>
                                <p className="text-xs text-gray-500 mt-1">Saves immediately upon selection.</p>
                            </div>
                        </div>
                    </div>

                    {/* Signature Upload Section */}
                    <div className="mb-6 bg-gray-50 p-4 border border-gray-200 rounded-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Signature</label>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            {invoiceFormData.signatureUrl ? (
                                <div className="w-32 h-32 border border-gray-300 rounded-md bg-white flex items-center justify-center p-2 relative group">
                                    <img src={`${STATIC_URL}${invoiceFormData.signatureUrl}`} alt="Company Signature" className="max-w-full max-h-full object-contain mix-blend-multiply" />
                                </div>
                            ) : (
                                <div className="w-32 h-32 border border-dashed border-gray-400 rounded-md bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                                    No Signature
                                </div>
                            )}
                            <div>
                                <label className="cursor-pointer bg-white px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition inline-flex items-center">
                                    {invoiceFormData.signatureUrl ? 'Change Signature' : 'Upload Signature'}
                                    <input type="file" className="hidden" accept="image/png, image/jpeg, image/jpg, image/svg+xml" onChange={handleSignatureUpload} />
                                </label>
                                <p className="text-xs text-gray-500 mt-2">Recommended: PNG with transparent background</p>
                                <p className="text-xs text-gray-500 mt-1">Saves immediately upon selection.</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Common: Company/Full Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {invoiceFormData.profileType === 'Entreprise' ? 'Company Name (Raison Sociale)' : 'Full Name'}
                            </label>
                            <input
                                type="text"
                                value={invoiceFormData.companyName}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, companyName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        {/* Common: Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={invoiceFormData.email}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, email: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        {/* Common: Phone */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="text"
                                value={invoiceFormData.phone}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, phone: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        {/* Auto-Entrepreneur only: CNIE */}
                        {invoiceFormData.profileType === 'Auto-Entrepreneur' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">CNIE (ID)</label>
                                <input
                                    type="text"
                                    value={invoiceFormData.cnie}
                                    onChange={e => setInvoiceFormData({ ...invoiceFormData, cnie: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>
                        )}
                        {/* Common: ICE */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ICE</label>
                            <input
                                type="text"
                                value={invoiceFormData.ice}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, ice: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        {/* Common: IF (Tax ID) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">IF (Tax ID)</label>
                            <input
                                type="text"
                                value={invoiceFormData.taxIf}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, taxIf: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        {/* Auto-Entrepreneur only: Professional Tax N° */}
                        {invoiceFormData.profileType === 'Auto-Entrepreneur' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax N°</label>
                                <input
                                    type="text"
                                    value={invoiceFormData.professionalTaxNo}
                                    onChange={e => setInvoiceFormData({ ...invoiceFormData, professionalTaxNo: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>
                        )}
                        {/* Entreprise only: RC (Registre de Commerce) */}
                        {invoiceFormData.profileType === 'Entreprise' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">RC (Registre de Commerce)</label>
                                <input
                                    type="text"
                                    value={invoiceFormData.rc}
                                    onChange={e => setInvoiceFormData({ ...invoiceFormData, rc: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>
                        )}
                        {/* Common: Address */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <textarea
                                value={invoiceFormData.address}
                                onChange={e => setInvoiceFormData({ ...invoiceFormData, address: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                rows={2}
                                required
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={invoiceLoading}
                            className={`px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition ${invoiceLoading ? 'opacity-70 cursor-wait' : ''
                                }`}
                        >
                            {invoiceLoading ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                </div>
            </form>

            {/* Expense Categories */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Tag size={20} className="text-blue-600" />
                        Expense Categories
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Manage categories used in expense forms.</p>
                </div>
                <div className="p-6 space-y-4">
                    {catMessage && (
                        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${catMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {catMessage.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                            {catMessage.text}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newCategory}
                            onChange={e => setNewCategory(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                            placeholder="New category name..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <button
                            onClick={handleAddCategory}
                            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
                        >
                            <Plus size={16} /> Add
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categories.map(cat => (
                            <div key={cat} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full text-sm text-gray-700">
                                <span>{cat}</span>
                                <button onClick={() => handleDeleteCategory(cat)} className="text-gray-400 hover:text-red-500 transition">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8 mb-12">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Database size={20} className="text-blue-600" />
                        Data Management
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Backup and restore your data to prevent loss.</p>
                </div>

                <div className="p-6 space-y-6">
                    {dataMessage && (
                        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${dataMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                            {dataMessage.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                            {dataMessage.text}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                                    <Download size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800 text-lg">Export Data</h3>
                                    <p className="text-sm text-gray-500">Download a full backup of your data.</p>
                                </div>
                            </div>
                            <button
                                onClick={handleExportData}
                                disabled={dataLoading}
                                className="w-full mt-2 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition font-medium disabled:opacity-50"
                            >
                                <Download size={18} />
                                {dataLoading ? 'Exporting...' : 'Export Backup (JSON)'}
                            </button>
                        </div>

                        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-orange-100 rounded-full text-orange-600">
                                    <Upload size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800 text-lg">Import Data</h3>
                                    <p className="text-sm text-gray-500">Restore your data from a JSON backup.</p>
                                </div>
                            </div>
                            <div className="relative">
                                <label className={`w-full mt-2 flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 transition font-medium cursor-pointer ${dataLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <Upload size={18} />
                                    {dataLoading ? 'Importing...' : 'Import Backup (JSON)'}
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".json"
                                        onChange={handleImportData}
                                        disabled={dataLoading}
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-red-500 mt-3 flex items-start gap-1">
                                <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                                <span>Caution: Restoring data will overwrite your current database state for this account.</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
